import type { Home, IncidentReport } from '@lmc/ts-common'
import { loadImageAsDataUrl } from 'components/pdf/images'
import CONSTANTS from 'utils/constants'
import { GOOGLE_MAPS_STATIC_MAP_BASE_URL } from 'utils/googleMapsApiUrls'
import {
  actionAttachments,
  type IncidentCaseAttachment,
  writtenFollowUpAttachments
} from '../attachments/caseAttachments'
import {
  hasWrittenFollowUp,
  savedDutyOfCandour,
  savedFollowUpActions
} from '../view/readRules'

/** The pictures a case's PDF prints: its image attachments and its GPS map. */
export type CaseImages = {
  /** Data URLs by attachment key; a file missing here prints by name. */
  attachments: ReadonlyMap<string, string>
  gpsMap: string | undefined
}

export const NO_CASE_IMAGES: CaseImages = {
  attachments: new Map(),
  gpsMap: undefined
}

const IMAGE_NAME = /\.(gif|jpe?g|png|webp)$/i

const isImage = ({ mimetype, name }: IncidentCaseAttachment) =>
  mimetype ? mimetype.startsWith('image/') : IMAGE_NAME.test(name)

/** The case's saved files that are pictures and can be fetched. */
const imageAttachments = (report: IncidentReport) => {
  const communication = savedDutyOfCandour(report)?.communication
  return [
    ...savedFollowUpActions(report).flatMap(actionAttachments),
    ...(hasWrittenFollowUp(communication)
      ? writtenFollowUpAttachments(communication)
      : [])
  ].filter(
    (file): file is IncidentCaseAttachment & { url: string } =>
      Boolean(file.url) && isImage(file)
  )
}

/**
 * The log's location as Google's static map, as the case view and the v1 PDF
 * show it; only when the home records locations and the log has one.
 */
export const gpsMapUrl = (report: IncidentReport, home: Home | undefined) => {
  const { latitude, longitude } = report.log
  // By presence: 0 is a real coordinate.
  if (
    !home?.features?.geoLocation?.enabled ||
    latitude == null ||
    longitude == null
  ) {
    return undefined
  }
  return `${GOOGLE_MAPS_STATIC_MAP_BASE_URL}?center=${latitude},${longitude}&zoom=19&size=600x300&markers=${latitude},${longitude}&key=${CONSTANTS.VITE_REACT_APP_GOOGLE_MAPS_API_KEY}`
}

/** A picture that can't be fetched or read is left out, never the download. */
const loadOrSkip = (url: string) =>
  loadImageAsDataUrl(url).catch(() => undefined)

export const loadCaseImages = async (
  report: IncidentReport,
  home: Home | undefined
): Promise<CaseImages> => {
  const files = imageAttachments(report)
  const mapUrl = gpsMapUrl(report, home)
  const [gpsMap, ...loaded] = await Promise.all([
    mapUrl ? loadOrSkip(mapUrl) : undefined,
    ...files.map((file) => loadOrSkip(file.url))
  ])

  return {
    attachments: new Map(
      files.flatMap((file, index) => {
        const src = loaded[index]
        return src ? [[file.key, src] as const] : []
      })
    ),
    gpsMap
  }
}
